// MedicationTable.js
import React, { useEffect, useState } from 'react';
import {
  Table, Space, Typography, Tag, Input, Button, message, Popconfirm, Modal
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, ShoppingCartOutlined, DownloadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { getCategoryColor } from '../utils/theme';
import { MedicationAPI } from '../MedicationAPI';
import AddMedicationForm from './AddMedicationForm';
import BulkUploadPopup from './popup/BulkUploadPopup';

const { Title, Text } = Typography;

const MedicationTable = () => {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);

  const fetchMedications = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const response = await MedicationAPI.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search,
      });
      setMedications(response.list);
      setPagination({
        current: response.pageInfo.page,
        pageSize: response.pageInfo.pageSize,
        total: response.pageInfo.totalRows,
      });
    } catch (error) {
      message.error('Failed to fetch medications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedications();
  }, []);

  const handleTableChange = (paginationConfig) => {
    fetchMedications(paginationConfig.current, paginationConfig.pageSize, searchTerm);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    fetchMedications(1, pagination.pageSize, value);
  };

  const openModal = (medication = null) => {
    setEditingMedication(medication);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingMedication(null);
  };

  const onFormSubmitSuccess = async () => {
    closeModal();
    try {
      const response = await MedicationAPI.getAll({ limit: 1, offset: 0, search: searchTerm });
      const totalRows = response.pageInfo.totalRows;
      const pageSize = pagination.pageSize;
      const lastPage = Math.ceil(totalRows / pageSize);

      setPagination((prev) => ({
        ...prev,
        current: lastPage,
        total: totalRows,
      }));

      fetchMedications(lastPage, pageSize, searchTerm);
    } catch (err) {
      message.error("Failed to refresh data after save.");
    }
  };

  const exportToExcel = async () => {
  try {
    const allMedications = [];
    const pageSize = 100; // Adjust based on API limits
    let offset = 0;
    let totalRows = 0;
    let fetchedAll = false;

    while (!fetchedAll) {
      const response = await MedicationAPI.getAll({
        limit: pageSize,
        offset,
        search: '',
      });

      const batch = response.list || [];
      allMedications.push(...batch);

      totalRows = response.pageInfo?.totalRows || 0;
      offset += pageSize;

      if (allMedications.length >= totalRows) {
        fetchedAll = true;
      }
    }

    if (allMedications.length === 0) {
      return message.info("No medications available to export.");
    }

    // Optional: map and format data
    const formattedData = allMedications.map((med) => ({
      ID: med.rec_Id,
      Name: med.name,
      Generic: med.generic,
      Quantity: med.qty,
      'Price per Strip': med.price_per_strip,
      'Total Price': med.price,
      'Expiry Date': med.expiry_date,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Medications");

    XLSX.writeFile(workbook, "medications.xlsx");
    message.success("Exported all medications successfully!");
  } catch (error) {
    console.error("Export error:", error);
    message.error("Failed to export all medications.");
  }
};


  const medicationColumns = [
    {
      title: 'ID',
      dataIndex: 'rec_Id',
      key: 'rec_Id',
      width: 70,
      render: (text) => <Text>{text || 'N/A'}</Text>,
    },
    {
      title: 'Medication',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Generic',
      dataIndex: 'generic',
      key: 'generic',
      width: 120,
      render: (generic) => <Tag color={getCategoryColor(generic)}>{generic}</Tag>,
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      key: 'qty',
      width: 80,
      render: (stock, record) => (
        <Space>
          <Text>{stock}</Text>
          {stock < record.reorder && <Tag color="red">Low</Tag>}
        </Space>
      ),
    },
    {
      title: 'Price per Strip',
      dataIndex: 'price_per_strip',
      key: 'price_per_strip',
      width: 130,
      render: (value) => `$${value != null ? parseFloat(value).toFixed(2) : '0.00'}`,
    },
    {
      title: 'Total Price',
      dataIndex: 'price',
      key: 'price',
      width: 130,
      render: (price) => `$${price != null ? parseFloat(price).toFixed(2) : '0.00'}`,
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 140,
      render: (text) => <Text>{text || 'N/A'}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space wrap>
          <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => openModal(record)}>Update</Button>
          <Popconfirm
            title="Are you sure you want to delete this medication?"
            onConfirm={async () => {
              try {
                await MedicationAPI.delete(record.rec_Id);
                message.success("Medication deleted successfully");
                fetchMedications(pagination.current, pagination.pageSize, searchTerm);
              } catch (error) {
                message.error("Failed to delete medication");
              }
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small">Delete</Button>
          </Popconfirm>
          <Button type="default" icon={<ShoppingCartOutlined />} size="small">Order</Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>Medication Inventory</Title>
        <Space>
          <Input
            placeholder="Search medications..."
            prefix={<SearchOutlined />}
            style={{ width: 250, backgroundColor: '#fff', borderColor: '#d9d9d9' }}
            onChange={(e) => handleSearch(e.target.value)}
            value={searchTerm}
          />
          <Button style={{ backgroundColor: '#fff', borderColor: '#d9d9d9', color: '#1677ff' }} onClick={() => setBulkUploadVisible(true)}>
            Bulk Upload
          </Button>
          <Button icon={<DownloadOutlined />} style={{ backgroundColor: '#fff', borderColor: '#d9d9d9', color: '#1677ff' }} onClick={exportToExcel}>
            Export
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Add Medication
          </Button>
        </Space>
      </div>

      <Table
        columns={medicationColumns}
        dataSource={medications}
        rowKey="rec_Id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total) => `Showing ${medications.length} of ${total} medications`,
        }}
        scroll={{ y: 390 }}
        onChange={handleTableChange}
      />

      <Modal
        title={null}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        width={800}
        centered
        maskClosable={false}
        keyboard
        maskStyle={{ backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        bodyStyle={{ backgroundColor: '#fff', borderRadius: 16, padding: '32px 32px 24px' }}
      >
        <div style={{ marginBottom: 8 }}>
          <Typography.Title level={4} style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {editingMedication ? 'Update Medication' : 'Add Medication'}
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Press <b>Ctrl + C</b> to Save &nbsp;|&nbsp; <b>ESC</b> to Cancel
          </Typography.Text>
        </div>
        <AddMedicationForm medication={editingMedication} onSuccess={onFormSubmitSuccess} onCancel={closeModal} />
      </Modal>

      <BulkUploadPopup visible={bulkUploadVisible} onClose={() => setBulkUploadVisible(false)} onUploadSuccess={onFormSubmitSuccess} />
    </>
  );
}; 

export default MedicationTable;
